/**
 * The per-pool notifications screen, addressed at
 * `/pools?poolId=<id>&view=notifications`. Two reads drive it: `POST
 * /pool/get` for the pool's name and `POST
 * /notification-settings/get-pool` for the Pools-group alert rows and
 * whether each one is overridden.
 */
import {
  useNotificationSettingsClearPoolOverride,
  useNotificationSettingsGetPool,
  useNotificationSettingsSetPoolAlertChannel,
} from "@sportsmet/notification-sdk/private";
import { usePoolGet } from "@sportsmet/pool-sdk/private";
import { Button, InlineBanner, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useEffect } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import {
  isPoolNoAccessCode,
  POOL_NO_ACCESS_ACTION_LABEL,
  POOL_NO_ACCESS_BODY,
  POOL_NO_ACCESS_TITLE,
} from "../../errors/poolAccess";
import { PoolAlertRow } from "./PoolAlertRow";
import {
  POOL_NOTIFICATIONS_BACK_LABEL,
  POOL_NOTIFICATIONS_EMPTY_MESSAGE,
  POOL_NOTIFICATIONS_FAILED_MESSAGE,
  POOL_NOTIFICATIONS_INTRO,
  POOL_NOTIFICATIONS_LOADING_LABEL,
  POOL_NOTIFICATIONS_RETRY_LABEL,
  poolNotificationsTitle,
  resolvePoolNotificationsState,
} from "./poolNotificationsState";

// `typePoolAlertRowModel["alertKind"]` comes from `alertKindLabels.ts` and
// spans every alert kind the account-level screen renders, not only the
// Pools group this screen's rows are always drawn from. The pool-scoped
// mutations accept the narrower 14-value Pools-group enum only. Derived from
// the mutation hook's own parameter type, the same way `alertKindLabels.ts`
// derives its types from the generated hook's return type rather than
// reaching past the package's barrel export.
type typeSetChannelHook = ReturnType<
  typeof useNotificationSettingsSetPoolAlertChannel
>;
type typePoolScopedAlertKind = Parameters<
  typeSetChannelHook["mutate"]
>[0]["alertKind"];

export const PoolNotificationsPane = ({
  poolId,
  onGoBack,
}: {
  poolId: string;
  onGoBack: () => void;
}): ReactNode => {
  const pool = usePoolGet({ poolId });
  const settings = useNotificationSettingsGetPool({ poolId });

  useEffect(() => {
    const code = extractErrorCode(pool.data);
    if (code !== null && !isPoolNoAccessCode(code)) {
      showApiError(pool.data, undefined, pool.dataUpdatedAt);
    }
  }, [pool.dataUpdatedAt, poolId]);

  useEffect(() => {
    const code = extractErrorCode(settings.data);
    if (code !== null && !isPoolNoAccessCode(code)) {
      showApiError(settings.data, undefined, settings.dataUpdatedAt);
    }
  }, [settings.dataUpdatedAt, poolId]);

  const state = resolvePoolNotificationsState({
    isLoading: pool.isLoading || settings.isLoading,
    isTransportError: pool.isError || settings.isError,
    errorCode: extractErrorCode(pool.data) ?? extractErrorCode(settings.data),
    poolName: pool.data?.data?.pool.name ?? null,
    alerts: settings.data?.data?.alerts ?? null,
  });

  const setChannel = useNotificationSettingsSetPoolAlertChannel({
    onSuccess: (result) => {
      showApiError(result);
    },
  });
  const clearOverride = useNotificationSettingsClearPoolOverride({
    onSuccess: (result) => {
      showApiError(result);
    },
  });

  return (
    <div className="flex flex-col gap-3" data-testid="pool-notifications-pane">
      <div
        className="flex items-center gap-2"
        data-testid="pool-notifications-header"
      >
        <Button variant="ghost" size="sm" onClick={onGoBack}>
          {POOL_NOTIFICATIONS_BACK_LABEL}
        </Button>
        {state.phase === "ready" || state.phase === "empty" ? (
          <h2 className="text-h2 text-ink">
            {poolNotificationsTitle(state.poolName)}
          </h2>
        ) : null}
      </div>
      {state.phase === "loading" ? (
        <div data-testid="pool-notifications-loading">
          <Skeleton shape="title" label={POOL_NOTIFICATIONS_LOADING_LABEL} />
          <Skeleton shape="card" />
          <Skeleton shape="card" />
          <Skeleton shape="card" />
        </div>
      ) : null}
      {state.phase === "failed" ? (
        <div data-testid="pool-notifications-failed">
          <InlineBanner
            variant="warning"
            message={POOL_NOTIFICATIONS_FAILED_MESSAGE}
            action={{
              label: POOL_NOTIFICATIONS_RETRY_LABEL,
              onAction: () => {
                void pool.refetch();
                void settings.refetch();
              },
            }}
          />
        </div>
      ) : null}
      {state.phase === "noAccess" ? (
        <div data-testid="pool-notifications-no-access">
          <p className="text-body text-ink">{POOL_NO_ACCESS_TITLE}</p>
          <p className="text-body text-ink-secondary">{POOL_NO_ACCESS_BODY}</p>
          <Button variant="secondary" size="md" onClick={onGoBack}>
            {POOL_NO_ACCESS_ACTION_LABEL}
          </Button>
        </div>
      ) : null}
      {state.phase === "empty" ? (
        <div data-testid="pool-notifications-empty">
          <p className="text-body text-ink-secondary">
            {POOL_NOTIFICATIONS_EMPTY_MESSAGE}
          </p>
        </div>
      ) : null}
      {state.phase === "ready" ? (
        <>
          <p
            className="text-body text-ink-secondary"
            data-testid="pool-notifications-intro"
          >
            {POOL_NOTIFICATIONS_INTRO}
          </p>
          {state.groups.map((group) => (
            <section
              key={group.group}
              data-testid={`pool-alert-group-${group.group}`}
            >
              <h3 className="text-body-strong text-ink">{group.header}</h3>
              {group.rows.map((row) => {
                const pendingChannel =
                  setChannel.isPending &&
                  setChannel.variables.alertKind === row.alertKind
                    ? setChannel.variables.channel
                    : null;
                const isResetPending =
                  clearOverride.isPending &&
                  clearOverride.variables.alertKind === row.alertKind;
                return (
                  <PoolAlertRow
                    key={row.alertKind}
                    row={row}
                    pendingChannel={pendingChannel}
                    isResetPending={isResetPending}
                    onToggleChannel={({ channel, isEnabled }) => {
                      setChannel.mutate({
                        poolId,
                        alertKind: row.alertKind as typePoolScopedAlertKind,
                        channel,
                        isEnabled,
                      });
                    }}
                    onReset={() => {
                      clearOverride.mutate({
                        poolId,
                        alertKind: row.alertKind as typePoolScopedAlertKind,
                      });
                    }}
                  />
                );
              })}
            </section>
          ))}
        </>
      ) : null}
    </div>
  );
};
